import React from "react";
import { Box, Typography, Button, useTheme, Container, Stack, alpha, Link } from "@mui/material";
import { config } from "../config";

interface ConnectWalletPromptProps {
  onConnect: () => void;
}

const ConnectWalletPrompt: React.FC<ConnectWalletPromptProps> = ({ onConnect }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, transparent 70%)`,
          filter: "blur(60px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 0%, transparent 70%)`,
          filter: "blur(60px)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          {/* Logo */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.2
              )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              mb: 2,
              p: 2,
            }}
          >
            <img
              src="/assets/icons/wiKeyIcon3.png"
              alt="wiKey Logo"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Main heading */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Welcome to WiKey
          </Typography>

          {/* Description */}
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: "90%",
            }}
          >
            Connect your WiKey wallet app to access your secure digital assets, manage your
            portfolio, and take control of your decentralized finances.
          </Typography>

          {/* Benefits list */}
          <Box
            sx={{
              mt: 2,
              width: "100%",
              maxWidth: 400,
            }}
          >
            <Stack spacing={2} alignItems="flex-start">
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "left",
                  }}
                >
                  Secure and decentralized asset management
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "left",
                  }}
                >
                  Full control over your transactions
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "left",
                  }}
                >
                  Multi-signature safe management
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Connect button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onConnect}
            sx={{
              mt: 4,
              px: 6,
              py: 1.5,
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              "&:hover": {
                boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Connect Wallet
          </Button>

          {/* Don't have app message */}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                mb: 1,
                fontSize: "0.875rem",
              }}
            >
              Don't have the WiKey wallet app?
            </Typography>
            <Link
              href={config.appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: theme.palette.primary.main,
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Download WiKey Wallet
            </Link>
          </Box>

          {/* Footer note */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 2,
              opacity: 0.7,
              fontSize: "0.875rem",
            }}
          >
            Your wallet connection is secure and private. We never store your private keys.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default ConnectWalletPrompt;
